/* eslint-disable import/prefer-default-export */
import { NotificationItem, NotificationsApi } from '@deckee/api-app-main';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getMemberAlerts } from '../../../member/alerts';
import { AppAsyncThunkAPI } from '../..';

import apiConfiguration from '../../../backend/apiConfiguration';
import { selectCurrentMemberId } from '../backend-service';
import { NotificationModel } from './models';
import { getSelectedNotification } from './selectors';
import { AlertItem } from '../../../../components/features/Notifications/NotificationsHomePage/Tabs/AlertsPage';

const api = new NotificationsApi(apiConfiguration);

export const setIsLoading = createAction(
  '[Notifications] Loading notifications',
  (isLoading: boolean) => ({ payload: isLoading }),
);

export const setNotifications = createAction(
  '[Notifications] set notifications',
  (notifications: (NotificationItem | AlertItem)[]) => {
    const notificationsDictionary = notifications.reduce(
      (acc, notification) => ({
        ...acc,
        [notification.id]: notification,
      }),
      {} as { [key: string]: NotificationModel | { id: string } },
    );

    return {
      payload: notificationsDictionary,
    };
  },
);

export const setSelectedNotification = createAction(
  '[Notifications] Set selected notification',
  (id: string) => ({
    payload: id,
  }),
);

export const clearSelectedNotification = createAction(
  '[Notifications] Clear selected notification',
);

export const updateNotificationAsRead = createAction(
  '[Notifications] update notification as read',
  (notification: NotificationItem) => ({
    payload: {
      ...notification,
      read: true,
    } as NotificationItem,
  }),
);

export const fetchNotificationsForCurrentMember = createAsyncThunk<void, void>(
  '[Notifications] fetch all notifications for member',
  async (_, { dispatch, rejectWithValue, getState, signal }) => {
    dispatch(setIsLoading(true));

    // Get the query limit from the meta store
    const notificationFeedLimit =
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window?.DECKEE_CONFIG?.notificationFeedLimit || '50';
    // Fallback to a limit of 50 in case of null which would load all notifications which we don't want

    const memberId = selectCurrentMemberId(getState());
    if (!memberId) {
      rejectWithValue('No current member');
    } else {
      signal.throwIfAborted();
      try {
        const { data } = await api.getNotificationsForMember(
          {
            memberId,
            limit: notificationFeedLimit,
          },
          {
            signal,
          },
        );

        const { alerts } = await getMemberAlerts();

        const transformAlertToNotification = (alert: {
          // eslint-disable-next-line camelcase
          created_at: string | number | Date;
        }) => ({
          ...alert,
          createdAt: new Date(alert.created_at),
          notificationType: 'alert',
        });
        const transformedAlerts = alerts.map(transformAlertToNotification);

        dispatch(setNotifications([...data, ...transformedAlerts]));
      } catch (e: any) {
        if (!signal.aborted) {
          dispatch(setIsLoading(false));
          throw rejectWithValue(e.message);
        }
      }
      dispatch(setIsLoading(false));
    }
  },
);

export const setNotificationAsRead = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>(
  '[Notifications] set notification as read',
  async (_, { dispatch, rejectWithValue, getState }) => {
    dispatch(setIsLoading(true));

    const currentState = getState();
    const notification = getSelectedNotification(currentState);

    if (!notification) {
      rejectWithValue('No current notification');
    } else {
      try {
        const { success } = await api.markNotificationAsRead({
          notificationId: notification.id,
        });
        if (success) {
          dispatch(updateNotificationAsRead(notification));
        }
      } catch (e: any) {
        rejectWithValue(e?.message);
      }
      dispatch(setIsLoading(false));
    }
  },
);
